import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarClock, Plus, Play, Pencil, Trash2, History, Workflow, Bot, Power } from "lucide-react";
import ClientPicker from "@/components/ClientPicker";
import ScheduledTaskModal from "@/components/schedules/ScheduledTaskModal";
import ScheduledTaskRuns from "@/components/schedules/ScheduledTaskRuns";

const STATUS_TONE = { completed: "text-emerald-200", failed: "text-rose-200", running: "text-amber-200", queued: "text-white/60" };

/** Scheduled tasks — flows and AI agents that run on presets or cron. */
export default function ScheduledTasksPage() {
    const { hasPermission } = useAuth();
    const canEdit = hasPermission("schedule.edit");
    const canRun = hasPermission("schedule.run");
    const [params, setParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ kind: "", client_id: "", q: "" });
    const [modal, setModal] = useState(null);
    const [runsOf, setRunsOf] = useState(null);

    const load = async () => {
        try {
            const qs = new URLSearchParams();
            if (filters.kind) qs.set("kind", filters.kind);
            if (filters.client_id) qs.set("client_id", filters.client_id);
            if (filters.q) qs.set("q", filters.q);
            const { data } = await api.get(`/admin/scheduled-tasks?${qs.toString()}`);
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { api.get("/admin/scheduled-tasks/_meta").then(({ data }) => setMeta(data)).catch(() => setMeta(null)); }, []);
    useEffect(() => { load(); }, [filters.kind, filters.client_id, filters.q]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

    // Deep link from Flow / Agent editors: /scheduled-tasks?new=flow&target=<id>
    useEffect(() => {
        const kind = params.get("new");
        if (kind && canEdit) {
            setModal({ preset: { kind, target_id: params.get("target") || "" } });
            setParams({}, { replace: true });
        }
    }, [params, canEdit, setParams]);

    const runNow = async (t) => {
        try {
            await api.post(`/admin/scheduled-tasks/${t.id}/run`);
            toast.success(`Queued "${t.name}"`);
            setTimeout(load, 1500);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };
    const toggle = async (t) => {
        try {
            await api.patch(`/admin/scheduled-tasks/${t.id}`, { enabled: !t.enabled });
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };
    const remove = async (t) => {
        if (!window.confirm(`Delete scheduled task "${t.name}"? Its run history is kept.`)) return;
        try {
            await api.delete(`/admin/scheduled-tasks/${t.id}`);
            toast.success("Deleted");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    const stats = useMemo(() => ({
        enabled: items.filter((t) => t.enabled).length,
        failing: items.filter((t) => t.last_run_status === "failed").length,
    }), [items]);

    return (
        <div className="p-6 md:p-8 max-w-[1400px]" data-testid="scheduled-tasks-page">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1">Capabilities</div>
                    <h1 className="text-2xl md:text-3xl flex items-center gap-2"><CalendarClock className="w-6 h-6 text-[#c4b5fd]" /> Scheduled Tasks</h1>
                    <p className="text-sm text-white/55 mt-1 max-w-2xl">
                        Run <Link to="/flows" className="underline">flows</Link> and <Link to="/agents" className="underline">AI agents</Link> on presets or cron, for a chosen client.
                        SiteCentric connections exchange that client's key for a token once per run, reuse it, then log out.
                    </p>
                </div>
                <div className="flex items-center gap-3 text-xs text-white/55">
                    <span data-testid="scheduled-tasks-count">{items.length} tasks · {stats.enabled} enabled{stats.failing ? ` · ${stats.failing} failing` : ""}</span>
                    {canEdit && (
                        <button onClick={() => setModal({})} data-testid="scheduled-tasks-new"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-black rounded-md text-xs hover:bg-white/90">
                            <Plus className="w-3.5 h-3.5" /> New scheduled task
                        </button>
                    )}
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4">
                {[["", "All"], ["flow", "Flows"], ["agent", "AI agents"]].map(([v, l]) => (
                    <button key={v} onClick={() => setFilters({ ...filters, kind: v })} data-testid={`scheduled-tasks-filter-${v || "all"}`}
                        className={`px-2.5 py-1 rounded-full text-[11px] border ${filters.kind === v ? "bg-white/10 border-white/30" : "border-white/10 text-white/55 hover:text-white"}`}>{l}</button>
                ))}
                <ClientPicker compact includeSuperdomain anyOptionLabel="(any client)" value={filters.client_id} onChange={(v) => setFilters({ ...filters, client_id: v })} width="w-52" testId="scheduled-tasks-client-filter" />
                <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="search…" data-testid="scheduled-tasks-search"
                    className="bg-white/5 border border-white/10 rounded-full px-3 py-1 text-[11px] w-44" />
            </div>

            <div className="border border-white/10 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                    <thead className="bg-white/[0.03] text-[10px] uppercase tracking-wider text-white/50">
                        <tr>
                            <th className="text-left px-3 py-2.5">Task</th>
                            <th className="text-left px-3 py-2.5">Runs</th>
                            <th className="text-left px-3 py-2.5">Client</th>
                            <th className="text-left px-3 py-2.5">Schedule</th>
                            <th className="text-left px-3 py-2.5">Next</th>
                            <th className="text-left px-3 py-2.5">Last</th>
                            <th className="text-right px-3 py-2.5">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="px-3 py-8 text-center text-white/40">Loading…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={7} className="px-3 py-10 text-center text-white/40" data-testid="scheduled-tasks-empty">
                                No scheduled tasks yet. {canEdit && "Create one, or use “Schedule” from a flow or agent editor."}
                            </td></tr>
                        ) : items.map((t) => (
                            <tr key={t.id} className={`border-t border-white/5 hover:bg-white/[0.02] ${t.enabled ? "" : "opacity-60"}`} data-testid={`scheduled-task-row-${t.id}`}>
                                <td className="px-3 py-2.5">
                                    <div className="flex items-center gap-1.5">
                                        {t.kind === "flow" ? <Workflow className="w-3.5 h-3.5 text-[#c4b5fd]" /> : <Bot className="w-3.5 h-3.5 text-sky-200" />}
                                        <span data-testid={`scheduled-task-name-${t.id}`}>{t.name}</span>
                                        {!t.enabled && <span className="text-[9px] uppercase tracking-wider border border-white/15 rounded px-1 text-white/50">paused</span>}
                                    </div>
                                    <div className="text-[10px] text-white/40 pl-5">
                                        <Link to={t.kind === "flow" ? `/flows/${t.target_id}` : `/agents/${t.target_id}`} className="underline">{t.target_name}</Link>
                                        {t.context_tools?.length > 0 && ` · ${t.context_tools.length} context tool${t.context_tools.length > 1 ? "s" : ""}`}
                                    </div>
                                </td>
                                <td className="px-3 py-2.5 font-mono text-white/60">{t.run_count || 0}</td>
                                <td className="px-3 py-2.5 font-mono text-white/70">{t.client_id}{t.site_id ? ` / ${t.site_id}` : ""}</td>
                                <td className="px-3 py-2.5 text-white/75" data-testid={`scheduled-task-schedule-${t.id}`}>{t.schedule_text}</td>
                                <td className="px-3 py-2.5 font-mono text-white/60">{t.enabled && t.next_run_at ? t.next_run_at.slice(0, 16).replace("T", " ") : "—"}</td>
                                <td className={`px-3 py-2.5 ${STATUS_TONE[t.last_run_status] || "text-white/40"}`} data-testid={`scheduled-task-last-${t.id}`}>
                                    {t.last_run_status || "never"}
                                    {t.last_run_at && <div className="text-[10px] font-mono text-white/40">{t.last_run_at.slice(0, 16).replace("T", " ")}</div>}
                                </td>
                                <td className="px-3 py-2.5">
                                    <div className="flex items-center justify-end gap-1">
                                        <IconBtn title="Run history" onClick={() => setRunsOf(t)} testId={`scheduled-task-history-${t.id}`}><History className="w-3.5 h-3.5" /></IconBtn>
                                        {canRun && <IconBtn title="Run now" onClick={() => runNow(t)} testId={`scheduled-task-run-${t.id}`}><Play className="w-3.5 h-3.5" /></IconBtn>}
                                        {canEdit && <IconBtn title={t.enabled ? "Pause" : "Enable"} onClick={() => toggle(t)} testId={`scheduled-task-toggle-${t.id}`}><Power className="w-3.5 h-3.5" /></IconBtn>}
                                        {canEdit && <IconBtn title="Edit" onClick={() => setModal({ task: t })} testId={`scheduled-task-edit-${t.id}`}><Pencil className="w-3.5 h-3.5" /></IconBtn>}
                                        {canEdit && <IconBtn title="Delete" danger onClick={() => remove(t)} testId={`scheduled-task-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></IconBtn>}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {modal && (
                <ScheduledTaskModal task={modal.task} preset={modal.preset} meta={meta} onClose={() => setModal(null)}
                    onSaved={() => { setModal(null); load(); }} />
            )}
            {runsOf && <ScheduledTaskRuns task={runsOf} onClose={() => setRunsOf(null)} />}
        </div>
    );
}

function IconBtn({ children, onClick, title, testId, danger }) {
    return (
        <button onClick={onClick} title={title} data-testid={testId}
            className={`p-1.5 rounded-md border border-white/10 text-white/60 hover:text-white hover:bg-white/5 ${danger ? "hover:text-rose-300 hover:border-rose-300/30" : ""}`}>
            {children}
        </button>
    );
}
